import React, { useState } from "react";
import {
  ArrowRight, Check, X as XIcon, Building2, IndianRupee, Repeat, TrendingUp, Globe2, Users,
  Presentation, School, Share2, Megaphone, CalendarDays, Sparkles, ChevronDown, Quote, Rocket, CreditCard,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Reveal, SectionHeading, Eyebrow } from "../components/common";
import Seo from "../components/Seo";
import ApplyModal from "../components/ApplyModal";
import RazorpayButton from "../components/RazorpayButton";
import {
  partnerOpportunity, partnerHighlights, partnerPlans, partnerReceives, partnerChannels,
  businessTestimonials, partnerFaqs, pageImages,
} from "../mock";

const oppIcons = { Users, Building2, IndianRupee, Repeat, TrendingUp };
const channelIcons = { Globe2, Users, Presentation, School, Share2, Megaphone, CalendarDays };

export default function Partner() {
  const [apply, setApply] = useState({ open: false, model: "" });
  const [openFaq, setOpenFaq] = useState(0);
  const [payOpen, setPayOpen] = useState("");
  const openApply = (model = "") => setApply({ open: true, model });

  return (
    <div className="overflow-hidden">
      <Seo
        title="Start Your Ed-Tech Business | MapMyKid Partner Program"
        description="Launch your ed-tech brand with MapMyKid and massive potential. No office, staff or location needed. Introduce assessments online, in schools and social groups, and build a rewarding six-figure income."
      />

      {/* HERO */}
      <section className="relative hero-gradient pt-32 sm:pt-40 pb-16">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <Eyebrow>Start Your Business</Eyebrow>
            <h1 className="mt-6 text-[28px] sm:text-[52px] font-extrabold leading-[1.05] text-slate-900">
              Launch your <span className="brand-gradient-text">ed-tech brand</span> with massive potential.
            </h1>
            <p className="mt-5 text-[18px] leading-relaxed text-slate-600 max-w-xl">
              You do not need an office, staff or a location. Simply introduce MapMyKid online, in schools, online meetings, social media and WhatsApp groups, and conduct assessments. It is a mission with genuine social impact, empowering thousands of parents and schools, which is exactly why it can also become a strong, six-figure rewarding income for you.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
              <a href="#plans"><Button className="cta-gradient text-white rounded-full h-14 px-7 text-[17px] font-semibold w-full sm:w-auto">See Plans & Pricing <ArrowRight className="ml-2 h-5 w-5" /></Button></a>
              <Button onClick={() => openApply()} variant="outline" className="rounded-full h-14 px-7 text-[17px] font-semibold border-violet-200 text-violet-700 hover:bg-violet-50 w-full sm:w-auto">Apply Now</Button>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="rounded-[28px] overflow-hidden shadow-2xl shadow-violet-300/40 ring-1 ring-violet-100">
              <img src={pageImages.womanPresenting} alt="Education entrepreneur" className="h-[420px] w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 right-5 glass-card rounded-2xl px-5 py-4 shadow-xl animate-floaty">
              <p className="text-[22px] font-extrabold brand-gradient-text">6-Figure</p>
              <p className="text-[15px] text-slate-500">Income Potential</p>
            </div>
          </div>
        </div>
      </section>

      {/* OPPORTUNITY */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="The Opportunity" title="A business with real purpose and real scale." subtitle="Reach a market of millions of children and thousands of schools, empower parents and educators, and build a valuable ed-tech business from anywhere." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerOpportunity.map((o, i) => {
              const Icon = oppIcons[o.icon] || Sparkles;
              return (
                <Reveal key={o.title} delay={i * 80}>
                  <div className="group h-full rounded-3xl border border-violet-100 bg-white p-7 card-hover">
                    <span className="grid h-14 w-14 place-items-center rounded-2xl bg-violet-50 text-violet-600 transition-colors group-hover:cta-gradient group-hover:text-white"><Icon className="h-7 w-7" /></span>
                    <h3 className="mt-4 text-[19px] font-bold text-slate-900">{o.title}</h3>
                    <p className="mt-2 text-[16px] text-slate-600 leading-relaxed">{o.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-8">
            <div className="rounded-3xl border border-violet-100 bg-violet-50/40 p-6 sm:p-7 flex flex-wrap justify-center gap-x-6 gap-y-3">
              {partnerHighlights.map((h) => (
                <span key={h} className="flex items-center gap-2 text-[16px] font-semibold text-slate-800"><Check className="h-5 w-5 text-teal-500" /> {h}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* PLANS */}
      <section id="plans" className="section-pad soft-gradient">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Partner Plans & Pricing" title="Pick your plan and start earning." subtitle="Pay online and start instantly, or apply first to talk with our team. All plans are inclusive of GST." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {partnerPlans.map((p, i) => (
              <Reveal key={p.name} delay={i * 100}>
                <div className={`relative flex flex-col h-full rounded-[28px] border bg-white p-6 sm:p-8 card-hover ${p.popular ? "border-violet-300 ring-2 ring-violet-200" : "border-violet-100"}`}>
                  {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full cta-gradient px-4 py-1.5 text-[13px] font-bold text-white shadow-lg whitespace-nowrap">Most Popular</span>}
                  <p className="text-[15px] font-semibold text-violet-600">{p.tagline}</p>
                  <h3 className="mt-1 text-[24px] font-extrabold text-slate-900">{p.name}</h3>
                  <div className="mt-4 flex flex-wrap items-end gap-1">
                    {p.yearlyPrefix && <span className="mb-2 text-[16px] text-slate-500">{p.yearlyPrefix}</span>}
                    <span className="text-[30px] sm:text-[38px] font-extrabold text-slate-900">₹{p.yearly}</span>
                    <span className="mb-2 text-[16px] text-slate-500">/ year</span>
                  </div>
                  {p.quarterly && <p className="text-[16px] text-slate-500">or ₹{p.quarterly} / quarter</p>}
                  {p.priceNote && <p className="mt-1 text-[14px] text-slate-400">{p.priceNote}</p>}
                  <ul className="mt-6 space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f.t} className={`flex items-start gap-2.5 text-[16px] ${f.ok ? "text-slate-700" : "text-slate-400"}`}>
                        {f.ok ? <Check className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" /> : <XIcon className="mt-0.5 h-5 w-5 shrink-0 text-slate-300" />}
                        {f.t}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 space-y-2.5">
                    {p.canPay && p.rzpButtonId && (
                      payOpen === p.name ? (
                        <div className="pay-start-wrap rounded-2xl border border-violet-100 bg-violet-50/60 p-3.5">
                          <p className="mb-2 text-center text-[13px] font-semibold text-slate-500">Select the plan below to pay &amp; proceed</p>
                          <RazorpayButton buttonId={p.rzpButtonId} />
                        </div>
                      ) : (
                        <Button onClick={() => setPayOpen(p.name)} className="w-full cta-gradient text-white rounded-full h-12 text-[16px] font-semibold" data-testid={`pay-start-${p.name.split(" ")[0].toLowerCase()}`}>
                          <CreditCard className="mr-2 h-5 w-5" /> Pay &amp; Start Instantly
                        </Button>
                      )
                    )}
                    <Button onClick={() => openApply(p.name)} variant="outline" className="w-full rounded-full h-12 text-[16px] font-semibold border-violet-200 text-violet-700 hover:bg-violet-50">
                      {p.canPay ? "Or Apply Here" : "Apply"}
                    </Button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-center text-[15px] text-slate-500">No restriction on retail pricing, number of assessments, licenses or school packages. Design everything your way.</p>
        </div>
      </section>

      {/* BUSINESS TESTIMONIALS */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Partner Stories" title="Real people, real businesses, across India." subtitle="From Noida to Mumbai, everyday entrepreneurs are building a rewarding income with MapMyKid." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {businessTestimonials.map((t, i) => (
              <Reveal key={t.name} delay={(i % 2) * 100}>
                <div className="relative h-full rounded-3xl border border-violet-100 bg-white p-7 card-hover">
                  <Quote className="h-9 w-9 text-violet-200" />
                  <p className="mt-3 text-[17px] leading-relaxed text-slate-700">{t.quote}</p>
                  <div className="mt-6 flex items-center gap-3">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full cta-gradient text-white text-[20px] font-extrabold ring-2 ring-violet-100">{t.city.charAt(0)}</span>
                    <div>
                      <p className="text-[16px] font-bold text-slate-900">{t.name}</p>
                      <p className="text-[15px] text-slate-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT PRO PARTNERS RECEIVE */}
      <section className="section-pad soft-gradient">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Pro & Prime Pro Deliverables" title="Your own branded ed-tech setup." subtitle="Pro and Prime Pro partners receive a complete, owned digital infrastructure under their own brand." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {partnerReceives.map((r, i) => (
              <Reveal key={r} delay={(i % 3) * 70}>
                <div className="flex items-center gap-3 rounded-2xl border border-violet-100 bg-white p-5 card-hover">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-teal-50 text-teal-600"><Check className="h-5 w-5" /></span>
                  <span className="text-[16px] font-medium text-slate-800">{r}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CHANNELS */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Sell Through Any Channel" title="Reach parents and schools your way." subtitle="Market in schools or on digital and social media, capture leads and grow your own client base." />
          <div className="mt-14 flex flex-wrap justify-center gap-4">
            {partnerChannels.map((c, i) => {
              const Icon = channelIcons[c.icon] || Globe2;
              return (
                <Reveal key={c.t} delay={i * 60}>
                  <div className="group flex items-center gap-3 rounded-full border border-violet-100 bg-white px-5 sm:px-6 py-3.5 max-w-full card-hover">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-violet-50 text-violet-600 transition-colors group-hover:cta-gradient group-hover:text-white"><Icon className="h-5 w-5" /></span>
                    <span className="text-[16px] font-semibold text-slate-800">{c.t}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* BUSINESS FAQ */}
      <section className="section-pad soft-gradient">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Business FAQs" title="Everything you need to know." />
          <div className="mt-12 space-y-3">
            {partnerFaqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-violet-100 bg-white overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="flex w-full items-center justify-between gap-4 p-5 text-left">
                  <span className="text-[17px] font-semibold text-slate-900">{f.q}</span>
                  <ChevronDown className={`h-5 w-5 shrink-0 text-violet-600 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                <div className={`grid transition-all duration-300 ${openFaq === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                  <div className="overflow-hidden">
                    <p className="px-5 pb-5 text-[16px] leading-relaxed text-slate-600">{f.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[32px] cta-gradient px-6 py-14 sm:px-14 sm:py-16 text-center">
              <div className="absolute inset-0 grain opacity-70" />
              <div className="absolute -top-20 -right-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
              <div className="relative">
                <Rocket className="mx-auto h-10 w-10 text-white/90" />
                <h2 className="mt-4 text-[28px] sm:text-[40px] font-extrabold text-white leading-tight">Two minutes. One new business.</h2>
                <p className="mx-auto mt-4 max-w-2xl text-[18px] text-white/85">Fill the short application form and our team will help you get started.</p>
                <div className="mt-8 flex justify-center">
                  <Button onClick={() => openApply()} className="bg-white text-violet-700 hover:bg-violet-50 rounded-full h-14 px-8 text-[17px] font-bold">Apply Now <ArrowRight className="ml-2 h-5 w-5" /></Button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <ApplyModal open={apply.open} onClose={() => setApply({ open: false, model: "" })} defaultModel={apply.model} />
    </div>
  );
}
